import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Chip,
  Alert,
  CircularProgress
} from '@mui/material';
import Parse from 'parse';

const MICRO = 1000000;

const dialogPaperSx = {
  backgroundColor: '#2A2A2A',
  color: '#fff',
  minWidth: 520
};

const labelSx = { color: 'rgba(255,255,255,0.7)', mb: 0.5 };
const inputSx = {
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#FFC107' }
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#FFC107' }
};

const textAreaSx = {
  ...inputSx,
  '& .MuiOutlinedInput-root': {
    ...inputSx['& .MuiOutlinedInput-root'],
    alignItems: 'flex-start'
  },
  '& .MuiInputBase-inputMultiline': {
    maxHeight: 180,
    overflowY: 'auto'
  }
};

const compactPaytablePlaceholder = `(3, "H1"): 1000,
(4, "H1"): 4000,
(5, "H1"): 8000,
(3, "H2"): 40,
(4, "H2"): 150,
(5, "H2"): 750

# Mid symbols
(3, "M1"): 20,
(4, "M1"): 75,
(5, "M1"): 400`;

const normalizeCompactToken = (token) => {
  const trimmed = token.trim();
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return Number(trimmed);
  }
  return trimmed.replace(/^["']|["']$/g, '');
};

const parseCompactPaytable = (value) => {
  const withoutComments = value
    .split('\n')
    .map((line) => line.replace(/#.*$/, '').trim())
    .filter(Boolean)
    .join('\n');

  if (!withoutComments) return [];

  const pattern =
    /\(\s*("[^"]*"|'[^']*'|-?\d+(?:\.\d+)?)\s*,\s*("[^"]*"|'[^']*'|-?\d+(?:\.\d+)?)\s*\)\s*:\s*(-?\d+(?:\.\d+)?)/g;
  const entries = [];
  let match;

  while ((match = pattern.exec(withoutComments)) !== null) {
    entries.push({
      key: [normalizeCompactToken(match[1]), normalizeCompactToken(match[2])],
      value: Number(match[3])
    });
  }

  const leftover = withoutComments.replace(pattern, '').replace(/[\s,]+/g, '');
  if (!entries.length || leftover) {
    throw new Error('Paytable must be valid JSON or compact paytable format.');
  }

  return entries;
};

const parsePaytableInput = (value) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return { error: 'Please enter paytable values.' };
  }

  const candidates = [trimmed];

  if (!trimmed.startsWith('[')) {
    candidates.push(`[${trimmed}]`);
  }

  let parsed;
  let lastError;
  try {
    for (const candidate of candidates) {
      try {
        parsed = JSON.parse(candidate);
        lastError = null;
        break;
      } catch (error) {
        lastError = error;
      }
    }
  } catch (error) {
    lastError = error;
  }

  if (lastError || parsed === undefined) {
    try {
      return { value: parseCompactPaytable(trimmed) };
    } catch (error) {
      return { error: 'Paytable must be valid JSON or compact paytable format.' };
    }
  }

  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    parsed = [parsed];
  }

  if (!Array.isArray(parsed)) {
    return { error: 'Paytable must be a JSON array.' };
  }

  return { value: parsed };
};

const formatCompactPaytable = (paytable) =>
  paytable
    .map((entry) => {
      const [first, second] = entry.key;
      const firstValue = typeof first === 'number' ? first : `"${String(first)}"`;
      const secondValue = typeof second === 'number' ? second : `"${String(second)}"`;
      return `(${firstValue}, ${secondValue}): ${entry.value}`;
    })
    .join(',\n');

const formatPaytableForInput = (paytable) => {
  if (
    Array.isArray(paytable) &&
    paytable.every(
      (entry) =>
        entry &&
        typeof entry === 'object' &&
        Array.isArray(entry.key) &&
        entry.key.length === 2 &&
        Object.prototype.hasOwnProperty.call(entry, 'value')
    )
  ) {
    return formatCompactPaytable(paytable);
  }

  if (Array.isArray(paytable)) {
    return JSON.stringify(paytable, null, 2);
  }

  if (typeof paytable === 'string' && paytable.trim()) {
    try {
      const parsed = JSON.parse(paytable);
      return Array.isArray(parsed) ? JSON.stringify(parsed, null, 2) : paytable;
    } catch (error) {
      return paytable;
    }
  }

  return '[]';
};

export default function EditBetLevelsDialog({ open, onClose, record }) {
  const [paytableInput, setPaytableInput] = useState('');
  const [rawInput, setRawInput] = useState('');
  const [defaultBetInput, setDefaultBetInput] = useState('');
  const [stepBetInput, setStepBetInput] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (record) {
      setPaytableInput(formatPaytableForInput(record.paytable));
      const levels = (record.betLevels || []).map((v) => (v / MICRO).toString());
      setRawInput(levels.join(', '));
      setDefaultBetInput(
        record.defaultBetLevel != null ? (record.defaultBetLevel / MICRO).toString() : '1'
      );
      setStepBetInput(record.stepBet != null ? record.stepBet.toString() : '10000');
      setError('');
    }
  }, [record, open]);

  const parsedLevels = () => {
    return rawInput
      .split(/[\s,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => parseFloat(s));
  };

  const validate = () => {
    const paytableResult = parsePaytableInput(paytableInput);
    if (paytableResult.error) return paytableResult.error;
    const levels = parsedLevels();
    if (levels.length === 0) return 'Enter at least one bet level.';
    if (levels.some(isNaN)) return 'All values must be valid numbers.';
    if (levels.some((v) => v <= 0)) return 'All values must be positive.';
    const def = parseFloat(defaultBetInput);
    if (isNaN(def) || def <= 0) return 'Default bet level must be a positive number.';
    return '';
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const levels = parsedLevels();
      const paytableResult = parsePaytableInput(paytableInput);
      if (paytableResult.error) {
        setError(paytableResult.error);
        setSaving(false);
        return;
      }
      const betLevelsMicro = levels.map((v) => Math.round(v * MICRO));
      const defaultBetMicro = Math.round(parseFloat(defaultBetInput) * MICRO);
      const stepBet = parseInt(stepBetInput, 10) || 10000;

      await Parse.Cloud.run('saveGameBetConfig', {
        objectId: record.id,
        gameSlug: record.gameSlug,
        gameName: record.gameName,
        paytable: paytableResult.value,
        betLevels: betLevelsMicro,
        minBet: betLevelsMicro[0],
        maxBet: betLevelsMicro[betLevelsMicro.length - 1],
        defaultBetLevel: defaultBetMicro,
        stepBet
      });
      onClose(true);
    } catch (e) {
      setError(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const previewLevels = parsedLevels().filter((v) => !isNaN(v) && v > 0);

  return (
    <Dialog open={open} onClose={() => onClose(false)} PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={{ color: '#FFC107', fontWeight: 700 }}>
        Edit Configurations
        {record && (
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
            {record.gameName} ({record.gameSlug})
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        <Box>
          <Typography variant="caption" sx={labelSx}>
            Paytable
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={6}
            maxRows={10}
            value={paytableInput}
            onChange={(e) => setPaytableInput(e.target.value)}
            placeholder={compactPaytablePlaceholder}
            sx={textAreaSx}
          />
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.5, display: 'block' }}
          >
            {
              'Enter values like (3, "H1"): 1000 or paste valid JSON. The system stores them as a paytable array automatically.'
            }
          </Typography>
        </Box>

        <Box>
          <Typography variant="caption" sx={labelSx}>
            Bet Levels (standard units, comma or space separated)
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={3}
            value={rawInput}
            onChange={(e) => setRawInput(e.target.value)}
            placeholder="0.5, 1, 2, 3, 4, 5, 10, 20"
            sx={inputSx}
          />
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.5, display: 'block' }}
          >
            Values are in standard currency units (e.g. 0.5 = $0.50). Stored as micro-units
            internally.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Box flex={1}>
            <Typography variant="caption" sx={labelSx}>
              Default Bet Level
            </Typography>
            <TextField
              fullWidth
              value={defaultBetInput}
              onChange={(e) => setDefaultBetInput(e.target.value)}
              placeholder="1"
              sx={inputSx}
            />
          </Box>
          <Box flex={1}>
            <Typography variant="caption" sx={labelSx}>
              Step Bet (micro-units)
            </Typography>
            <TextField
              fullWidth
              value={stepBetInput}
              onChange={(e) => setStepBetInput(e.target.value)}
              placeholder="10000"
              sx={inputSx}
            />
          </Box>
        </Box>

        {previewLevels.length > 0 && (
          <Box>
            <Typography variant="caption" sx={{ ...labelSx, mb: 1 }}>
              Preview ({previewLevels.length} levels)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {previewLevels.map((v, i) => (
                <Chip
                  key={i}
                  label={v}
                  size="small"
                  sx={{ backgroundColor: '#333', color: '#FFC107', fontSize: '11px' }}
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={() => onClose(false)}
          sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'none' }}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{
            background: '#FFC107',
            color: '#0A0A0A',
            textTransform: 'none',
            fontWeight: 700,
            '&:hover': { backgroundColor: '#FFA000' }
          }}
        >
          {saving ? <CircularProgress size={18} sx={{ color: '#0A0A0A' }} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
